/**
 * Asks a signed-in member to accept mandatory documents they have not yet
 * accepted in their current version: Google sign-ups, existing members when
 * the documents first take effect, and everyone after a material update.
 * Shows nothing while the documents are drafts (the backend reports
 * needs: [] until they are final and enforcement is switched on).
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function LegalGate() {
  const { user, logout } = useAuth();
  const [state, setState] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    api.get('/legal/me').then((r) => setState(r.data)).catch(() => {});
  }, [user]);

  if (!state?.needs?.length) return null;

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      const documents = Object.fromEntries(state.needs.map((d) => [d, state.versions[d]]));
      const r = await api.post('/legal/accept', { documents });
      setState(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const titles = { terms: 'Terms of Service', guidelines: 'Community Guidelines' };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="legal-gate-title" data-testid="legal-gate">
      <div className="w-full max-w-md max-h-[calc(var(--app-h,100vh)-24px)] overflow-y-auto rounded-2xl border border-[hsl(var(--mg-rune)/0.3)] bg-card p-6 shadow-2xl">
        <h2 id="legal-gate-title" className="font-heading text-xl font-bold">Before you continue</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Midgard's {state.needs.map((d) => titles[d] || d).join(' and ')} {state.needs.length > 1 ? 'have' : 'has'} been
          published or updated. Please read and accept {state.needs.length > 1 ? 'them' : 'it'} to keep using your account.
        </p>
        <ul className="mt-3 space-y-1 text-sm">
          {state.needs.map((d) => (
            <li key={d}><a href={`/legal/${d}`} target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">{titles[d] || d}</a> <span className="text-muted-foreground">(version {state.versions[d]})</span></li>
          ))}
        </ul>
        <label className="mt-4 flex items-start gap-2.5 text-sm cursor-pointer">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4" data-testid="legal-gate-checkbox" />
          <span>I have read and agree to these documents.</span>
        </label>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={logout} className="mg-btn mg-btn-ghost h-10 px-4 text-sm">Log out</button>
          <button type="button" onClick={accept} disabled={!agreed || busy} className="mg-btn mg-btn-primary h-10 px-5 text-sm" data-testid="legal-gate-accept">
            {busy ? 'Saving…' : 'Accept and continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
